import React, { useEffect, useMemo, useRef, useState } from 'react';

const Icon = ({ children, className = 'h-4 w-4' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const DOC_TYPES = [
    { key: 'registration', label: 'Registration' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'invoice', label: 'Invoice' },
    { key: 'inspection', label: 'Inspection' },
    { key: 'manual', label: 'Manual' },
    { key: 'photo', label: 'Photo' },
    { key: 'receipt', label: 'Receipt' },
    { key: 'other', label: 'Other' },
];

const Dots = ({ step, total = 4 }) => (
    <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i < step ? 'w-6 bg-[#0b74b5]' : 'w-2.5 bg-[#d8e6f3]'}`} />
        ))}
    </div>
);

const getLabel = (v) => [v?.year, v?.make, v?.model].filter(Boolean).join(' ') + (v?.trim ? ` ${v.trim}` : '');

// 4-step "Add Document" wizard: vehicle → type → file reference → details + link to resolved case.
export default function AddDocumentWizard({ isOpen, onClose, vehicles = [], initialVehicleId, resolvedCases = [], onSave }) {
    const [step, setStep] = useState(1);
    const [vehicleId, setVehicleId] = useState(initialVehicleId);
    const [documentKind, setDocumentKind] = useState('');
    const [file, setFile] = useState(null);
    const [filename, setFilename] = useState('');
    const [title, setTitle] = useState('');
    const [provider, setProvider] = useState('');
    const [reference, setReference] = useState('');
    const [issued, setIssued] = useState('');
    const [expires, setExpires] = useState('');
    const [notes, setNotes] = useState('');
    const [linkedCaseId, setLinkedCaseId] = useState('');
    const [caseSearch, setCaseSearch] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        // Reset on open.
        setStep(1);
        setVehicleId(initialVehicleId);
        setDocumentKind('');
        setFile(null); setFilename('');
        setTitle(''); setProvider(''); setReference(''); setIssued(''); setExpires(''); setNotes('');
        setLinkedCaseId(''); setCaseSearch(''); setError(''); setSaving(false);
    }, [isOpen, initialVehicleId]);

    const casesForVehicle = useMemo(() => (
        (resolvedCases || []).filter((c) => !vehicleId || String(c.vehicleId) === String(vehicleId))
    ), [resolvedCases, vehicleId]);
    const filteredCases = useMemo(() => {
        const q = caseSearch.trim().toLowerCase();
        return q ? casesForVehicle.filter((c) => String(c.title || '').toLowerCase().includes(q)) : casesForVehicle;
    }, [casesForVehicle, caseSearch]);

    if (!isOpen) return null;

    const pickedFile = (f) => {
        if (!f) return;
        setFile(f);
        setFilename(f.name || '');
        if (!title) setTitle((f.name || '').replace(/\.[^.]+$/, ''));
    };

    const goToDetails = () => {
        if (!title) {
            const base = filename.replace(/\.[^.]+$/, '') || DOC_TYPES.find((t) => t.key === documentKind)?.label || '';
            setTitle(base);
        }
        setStep(4);
    };

    const handleSave = async () => {
        if (!title.trim()) { setError('Enter a title for this document.'); return; }
        setSaving(true);
        setError('');
        try {
            await onSave({
                vehicleId,
                documentKind: documentKind || 'other',
                file,
                filename: filename || title.trim(),
                title: title.trim(),
                provider: provider.trim(),
                reference: reference.trim(),
                issued,
                expires,
                notes: notes.trim(),
                linkedCaseId: linkedCaseId || null,
            });
        } catch (err) {
            setError(err?.message || 'Could not save the document. Please try again.');
            setSaving(false);
        }
    };

    const inputClass = 'mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]';

    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 px-4 py-4 sm:items-center" onClick={() => !saving && onClose?.()}>
            <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-[#d8e6f3] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="shrink-0 px-6 pt-5">
                    <div className="flex items-start justify-between gap-3">
                        <h2 className="text-lg font-black text-[#0b1b2e]">Add Document</h2>
                        <button type="button" onClick={() => !saving && onClose?.()} aria-label="Close" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#5f728a] transition hover:bg-[#f2f9ff]">
                            <Icon className="h-4 w-4"><path d="M18 6 6 18M6 6l12 12" /></Icon>
                        </button>
                    </div>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#5f728a]">Keep a document with its vehicle and optionally link it to a resolved diagnostic.</p>
                    <div className="mt-4"><Dots step={step} /></div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    {step === 1 && (
                        <div>
                            <p className="mb-3 text-[15px] font-black text-[#1f3350]">Which vehicle is this document for?</p>
                            <div className="space-y-2.5">
                                {vehicles.map((v) => {
                                    const selected = String(v.vehicle_id) === String(vehicleId);
                                    return (
                                        <button key={v.vehicle_id} type="button" onClick={() => setVehicleId(v.vehicle_id)}
                                            className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                                                selected ? 'border-[#0b74b5] bg-[#f2f9ff] ring-1 ring-[#b9ddf8]' : 'border-[#d8e6f3] bg-white hover:border-[#9fcdf0]'
                                            }`}>
                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${selected ? 'bg-[#0b74b5] text-white' : 'border border-[#d8e6f3] text-transparent'}`}>
                                                <Icon className="h-4 w-4"><path d="M20 6 9 17l-5-5" /></Icon>
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block truncate text-[15px] font-black text-[#182233]">{getLabel(v)}</span>
                                                {v.plate_number ? <span className="block text-xs font-semibold text-[#5f728a]">{v.plate_number}</span> : null}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <p className="mb-3 text-[15px] font-black text-[#1f3350]">What type of document is this?</p>
                            <div className="grid grid-cols-2 gap-2.5">
                                {DOC_TYPES.map((t) => {
                                    const selected = documentKind === t.key;
                                    return (
                                        <button key={t.key} type="button" onClick={() => setDocumentKind(t.key)}
                                            className={`rounded-xl border px-4 py-3 text-left text-sm font-black transition ${
                                                selected ? 'border-[#0b74b5] bg-[#f2f9ff] text-[#075c92] ring-1 ring-[#b9ddf8]' : 'border-[#d8e6f3] bg-white text-[#182233] hover:border-[#9fcdf0]'
                                            }`}>
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <p className="mb-3 text-[15px] font-black text-[#1f3350]">Attach a file (optional — you can skip this step).</p>
                            <div className="grid grid-cols-3 gap-2.5">
                                <button type="button" onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-2 py-4 text-center transition hover:border-[#9fcdf0] hover:bg-[#f8fbfe]">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff1f1] text-[#b42318]"><Icon className="h-5 w-5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></Icon></span>
                                    <span className="text-xs font-black text-[#182233]">Choose File</span>
                                    <span className="text-[10px] font-semibold text-[#5f728a]">PDF</span>
                                </button>
                                <button type="button" onClick={() => photoInputRef.current?.click()} className="flex flex-col items-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-2 py-4 text-center transition hover:border-[#9fcdf0] hover:bg-[#f8fbfe]">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef6fc] text-[#0b74b5]"><Icon className="h-5 w-5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></Icon></span>
                                    <span className="text-xs font-black text-[#182233]">Choose Photo</span>
                                    <span className="text-[10px] font-semibold text-[#5f728a]">Gallery</span>
                                </button>
                                <button type="button" onClick={() => cameraInputRef.current?.click()} className="flex flex-col items-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-2 py-4 text-center transition hover:border-[#9fcdf0] hover:bg-[#f8fbfe]">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#ecfff5] text-[#087a43]"><Icon className="h-5 w-5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></Icon></span>
                                    <span className="text-xs font-black text-[#182233]">Take Photo</span>
                                    <span className="text-[10px] font-semibold text-[#5f728a]">Camera</span>
                                </button>
                            </div>
                            {file && (
                                <p className="mt-3 flex items-center gap-2 rounded-xl border border-[#bde7d5] bg-[#ecfff5] px-3 py-2 text-sm font-bold text-[#087a43]">
                                    <Icon className="h-4 w-4"><path d="M20 6 9 17l-5-5" /></Icon>
                                    <span className="truncate">{filename}</span>
                                </p>
                            )}
                            <div className="mt-4">
                                <label className="text-sm font-black text-[#1f3350]">Or enter a filename manually</label>
                                <input type="text" value={filename} onChange={(e) => { setFilename(e.target.value); setFile(null); }} placeholder="e.g. registration-2026.pdf" className={inputClass} />
                            </div>
                            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => { pickedFile(e.target.files?.[0]); e.target.value = ''; }} />
                            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pickedFile(e.target.files?.[0]); e.target.value = ''; }} />
                            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pickedFile(e.target.files?.[0]); e.target.value = ''; }} />
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Title <span className="text-[#b42318]">*</span></label>
                                <input type="text" value={title} onChange={(e) => { setTitle(e.target.value); setError(''); }} placeholder="Document title" className={inputClass} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Provider</label>
                                    <input type="text" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="e.g. State Farm" className={inputClass} />
                                </div>
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Reference #</label>
                                    <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Policy / ID no." className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Issued</label>
                                    <input type="date" value={issued} onChange={(e) => setIssued(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Expires</label>
                                    <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Notes</label>
                                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes about this document" className={`${inputClass} resize-y`} />
                            </div>
                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Link to Resolved Case (optional)</label>
                                <input type="text" value={caseSearch} onChange={(e) => setCaseSearch(e.target.value)} placeholder="Search resolved cases..." className={inputClass} />
                                {filteredCases.length > 0 && (
                                    <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                                        {filteredCases.map((c) => {
                                            const selected = String(c.id) === String(linkedCaseId);
                                            return (
                                                <button key={c.id} type="button" onClick={() => setLinkedCaseId(selected ? '' : c.id)}
                                                    className={`block w-full rounded-xl border px-3 py-2 text-left transition ${
                                                        selected ? 'border-[#0b74b5] bg-[#f2f9ff] ring-1 ring-[#b9ddf8]' : 'border-[#d8e6f3] bg-white hover:border-[#9fcdf0]'
                                                    }`}>
                                                    <span className="block truncate text-sm font-black text-[#182233]">{c.title}</span>
                                                    {c.resolvedLabel ? <span className="block text-xs font-semibold text-[#5f728a]">{c.resolvedLabel}</span> : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {error && <p className="text-sm font-bold text-[#b42318]">{error}</p>}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[#edf3f8] px-6 py-4">
                    {step > 1 ? (
                        <button type="button" onClick={() => setStep((s) => s - 1)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-xl border border-[#d8e6f3] bg-white px-4 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60">
                            <Icon className="h-4 w-4"><path d="m15 18-6-6 6-6" /></Icon>
                            Back
                        </button>
                    ) : <span />}

                    {step === 1 && (
                        <button type="button" onClick={() => setStep(2)} disabled={!vehicleId} className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#095f90] disabled:cursor-not-allowed disabled:opacity-50">Next</button>
                    )}
                    {step === 2 && (
                        <button type="button" onClick={() => setStep(3)} disabled={!documentKind} className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#095f90] disabled:cursor-not-allowed disabled:opacity-50">Next</button>
                    )}
                    {step === 3 && (
                        <button type="button" onClick={goToDetails} className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#095f90]">{file || filename.trim() ? 'Next' : 'Skip'}</button>
                    )}
                    {step === 4 && (
                        <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#095f90] disabled:cursor-wait disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
                    )}
                </div>
            </div>
        </div>
    );
}
